/**
 * CopilotNotesPanel — renders ToxIntel Copilot™ conversation findings
 * that the user has pinned to this formulation.
 *
 * Each pinned note shows the original user question (for context), the
 * assistant's response, an optional user-supplied note, and an unpin
 * action. Lives on AssessmentPage so the regulatory record of "what the
 * Copilot said about this SKU" is preserved alongside the formal report.
 */
import React, { useState, useEffect, useCallback } from 'react';
import TI from './brand/TI';
import axios from 'axios';
import { toast } from 'sonner';
import { PushPin, Trash, ArrowSquareOut } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CopilotNotesPanel({ formulationId }) {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!formulationId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/copilot/formulations/${formulationId}/pins`,
        { withCredentials: true },
      );
      setPins(r.data?.pins || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [formulationId]);

  useEffect(() => { load(); }, [load]);

  const unpin = async (pinId) => {
    if (!window.confirm('Remove this Copilot note from the formulation?')) return;
    try {
      await axios.delete(`${API_URL}/api/copilot/pins/${pinId}`, { withCredentials: true });
      toast.success('Note removed');
      setPins(prev => prev.filter(p => p.id !== pinId));
    } catch (e) { toast.error('Could not remove the note'); }
  };

  if (loading) return null;
  if (pins.length === 0) {
    return (
      <div className="mb-8 p-4 border border-dashed border-gray-300 rounded-sm bg-white/50" data-testid="copilot-notes-empty">
        <div className="flex items-center gap-2 mb-1">
          <PushPin size={14} className="text-[#E63946]" weight="duotone" />
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#4B5563]">
            Copilot<sup className="text-[8px]">™</sup> Notes
          </p>
        </div>
        <p className="text-xs text-[#6B7280] leading-relaxed">
          No findings pinned yet. Open <a href="/copilot" className="text-[#E63946] hover:underline font-semibold">Copilot™</a> and use
          the "◆ Pin to formulation" action on any reply to durably attach a scientific
          finding here.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-8" data-testid="copilot-notes-panel">
      <div className="flex items-baseline gap-2 mb-3">
        <PushPin size={14} className="text-[#E63946]" weight="duotone" />
        <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#0B2447]">
          Copilot<sup className="text-[8px]">™</sup> Notes
        </h3>
        <span className="text-[10px] font-mono text-[#9CA3AF]">{pins.length} pinned</span>
      </div>
      <div className="space-y-3">
        {pins.map(p => <NoteCard key={p.id} pin={p} onUnpin={() => unpin(p.id)} />)}
      </div>
    </div>
  );
}

function NoteCard({ pin, onUnpin }) {
  return (
    <div className="border border-[#E63946]/25 bg-white p-4 rounded-sm" data-testid={`copilot-note-${pin.id}`}>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-[#E63946]">⌬ <TI color="#E63946" /> finding</span>
          {pin.session_title && (
            <span className="text-[10px] font-mono text-[#9CA3AF] truncate">· {pin.session_title}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] font-mono text-[#9CA3AF]">{new Date(pin.created_at).toLocaleDateString()}</span>
          <a href="/copilot" className="text-[#4B5563] hover:text-[#E63946]" title="Open Copilot">
            <ArrowSquareOut size={12} />
          </a>
          <button onClick={onUnpin}
            className="text-[#9CA3AF] hover:text-[#9B1C1C]"
            title="Remove note"
            data-testid={`copilot-note-unpin-${pin.id}`}>
            <Trash size={12} />
          </button>
        </div>
      </div>

      {pin.user_query && (
        <div className="mb-2 px-3 py-2 bg-[#F9FAFB] border-l-2 border-[#0B2447] text-[12px] text-[#374151]">
          <span className="text-[9px] font-mono uppercase tracking-wider text-[#9CA3AF] mr-2">Q</span>
          {pin.user_query}
        </div>
      )}

      <div className="text-[13px] leading-relaxed text-[#111827] whitespace-pre-wrap">
        {pin.content}
      </div>

      {pin.note && (
        <p className="mt-3 pt-3 border-t border-gray-100 text-[12px] italic text-[#6B7280]">
          <span className="font-bold not-italic text-[#374151]">Note:</span> {pin.note}
        </p>
      )}
    </div>
  );
}
